"use client"

import type React from "react"
import { useState } from "react"
import api from "../../libs/axios"
import toast from "react-hot-toast"
import Permissions from "./permissions"
import { FiX, FiKey, FiLoader } from "react-icons/fi"

interface CreateRoleOffcanvasProps {
    fetchRoles: () => void
    showCreateRoleCanvas: boolean
    setShowCreateRoleCanvas: (show: boolean) => void
}

interface RoleFormData {
    name: string
    description: string
    is_active: boolean
    permissions: number[]
}

const CreateRoleOffcanvas: React.FC<CreateRoleOffcanvasProps> = ({
    fetchRoles,
    showCreateRoleCanvas,
    setShowCreateRoleCanvas
}) => {
    const [formData, setFormData] = useState<RoleFormData>({
        name: "",
        description: "",
        is_active: true,
        permissions: [],
    })

    const [loading, setLoading] = useState(false)
    const [resetPermissions, setResetPermissions] = useState(false)

    const [errors, setErrors] = useState<{
        name?: string
        description?: string
        permissions?: string
    }>({})

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target
        const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))

        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }))
        }
    }

    const handlePermissionChange = (permissionId: number, checked: boolean) => {
        setFormData((prev) => ({
            ...prev,
            permissions: checked
                ? [...prev.permissions, permissionId]
                : prev.permissions.filter((id) => id !== permissionId),
        }))

        if (errors.permissions) {
            setErrors((prev) => ({
                ...prev,
                permissions: undefined,
            }))
        }
    }

    const handleModuleSelectAll = (module: string, checked: boolean) => {
        if (errors.permissions) {
            setErrors((prev) => ({
                ...prev,
                permissions: undefined,
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: {
            name?: string
            description?: string
            permissions?: string
        } = {}

        if (!formData.name.trim()) {
            newErrors.name = "Role name is required"
        } else if (formData.name.trim().length < 1) {
            newErrors.name = "Role name must be at least 1 character"
        } else if (formData.name.trim().length > 50) {
            newErrors.name = "Role name must not exceed 50 characters"
        }

        if (formData.description && formData.description.trim().length > 200) {
            newErrors.description = "Description must not exceed 200 characters"
        }

        // if (formData.permissions.length === 0) {
        //     newErrors.permissions = "Please select at least one permission"
        // }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                setLoading(true)

                // First create the role
                const roleResponse = await api.post('/roles', {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    is_active: formData.is_active
                })

                const newRoleId = roleResponse.data.role.id

                // Then assign permissions to the role
                if (formData.permissions.length > 0) {
                    await api.post(`/roles/${newRoleId}/permissions`, {
                        permission_ids: formData.permissions
                    })
                }

                toast.success("Role created successfully!")

                // Reset form
                setFormData({
                    name: "",
                    description: "",
                    is_active: true,
                    permissions: [],
                })

                fetchRoles()
                setErrors({})
                setShowCreateRoleCanvas(false)

                // Trigger reset of permissions component
                setResetPermissions(prev => !prev)

            } catch (error: any) {
                console.error("Error creating role:", error)

                if (error.response?.data?.errors?.name) {
                    toast.error(error.response.data.errors.name[0])
                } else if (error.response?.data?.message) {
                    toast.error(error.response.data.message)
                } else {
                    toast.error("Failed to create role")
                }
            } finally {
                setLoading(false)
            }
        } else {
            toast.error("Please fix the validation errors before submitting")
        }
    }

    const handleClose = () => {
        setShowCreateRoleCanvas(false)
        setErrors({})

        // Reset form data when closing
        setFormData({
            name: "",
            description: "",
            is_active: true,
            permissions: [],
        })

        // Trigger reset of permissions component
        setResetPermissions(prev => !prev)
    }

    return (
        <>
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showCreateRoleCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={handleClose}
            />

            <div
                className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showCreateRoleCanvas ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiKey className="mr-2" />
                        Create New Role
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.name ? "border-red-500" : "border-gray-300"
                                        }`}
                                    placeholder="Enter role name (e.g., Organizer, Scorer)"
                                    maxLength={50}
                                />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.description ? "border-red-500" : "border-gray-300"
                                        }`}
                                    placeholder="Describe the role and its responsibilities"
                                    maxLength={200}
                                />
                                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="rounded border-gray-300 w-4 h-4 text-primary-600 focus:ring-primary-500"
                                    id="is_active"
                                />
                                <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
                                    Active Role
                                </label>
                            </div>
                        </div>

                        {/* Permissions Component */}
                        <Permissions
                            selectedPermissions={formData.permissions}
                            onPermissionChange={handlePermissionChange}
                            onModuleSelectAll={handleModuleSelectAll}
                            errors={errors}
                            isVisible={showCreateRoleCanvas}
                            resetTrigger={resetPermissions}
                        />
                    </div>

                    <div className="border-t border-gray-200 p-6 mb-20">
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading}
                                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <FiLoader className="w-4 h-4 animate-spin inline mr-2" />
                                        Creating...
                                    </>
                                ) : (
                                    "Create Role"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CreateRoleOffcanvas